import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuthContext } from 'context/AuthContext';
import Header from 'components/Header';
import Button from 'components/Button';
import Popup from 'components/Popup';
import TextInput from 'components/TextInput';
import Dropdown from 'components/Dropdown';
import Table from 'components/Table';
import axios from 'config/axios';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import './index.css';

const ROLES = [
    'Admin',
    'Tutor'
];

function Instructors() {
    const [tutors, setTutors] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [search, setSearch] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [success, setSuccess] = useState('');
    const [failed, setFailed] = useState('');
    const [organization, setOrganization] = useState(null);
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [role, setRole] = useState('Tutor');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');

    const { user } = useAuthContext();

    const closeForm = () => {
        setFormOpen(false);
        setOrganization(organizations[0]);
        setEmail('');
        setFirstName('');
        setLastName('');
        setPassword('');
        setPhoneNumber('');
    }

    const navigate = useNavigate();
    const addTutorRef = useOutsideClickHandler(closeForm);
    const successRef = useOutsideClickHandler(() => setSuccess(''));
    const failedRef = useOutsideClickHandler(() => setFailed(''));

    const getTutors = () => {
        axios.get('tutors/tutors/')
            .then(res => {
                setTutors(res.data.tutors);
                setOrganizations(res.data.organizations);
                if (res.data.organizations.length > 0) {
                    setOrganization(res.data.organizations[0]);
                }
            })
            .catch(error => {
                console.log(error);
            });
    }

    const addTutor = () => {
        if (!firstName || !lastName || !email || !password) {
            setFailed('Please make sure you have filled in all the required fields.');
        }
        else if (password.length < 6) {
            setFailed('Password must be at least 6 characters long.');
        }
        else {
            axios.post('tutors/tutors/add/', {
                    organization: organization?.id,
                    email: email,
                    firstName: firstName,
                    lastName: lastName,
                    password: password,
                    countryCode: null,
                    phoneNumber: phoneNumber,
                    role: role
                })
                .then(res => {
                    closeForm();
                    setSuccess('Successfully added instructor!');
                    getTutors();
                })
                .catch(error => {
                    console.log(error);
                    if (error.response.status === 403) {
                        setFailed('Failed to add instructor. Instructor with email already exists.')
                    }
                    else {
                        setFailed('Failed to add instructor. Please make sure you have filled in all the fields correctly.');
                    }
                });
        }
    }

    useEffect(() => {
        getTutors();
    }, []);

    const newTutor = (
        <Popup
            title='New instructor'
            primaryButtonText='Save'
            primaryButtonOnClick={addTutor}
            secondaryButtonText='Cancel'
            secondaryButtonOnClick={closeForm}
            innerRef={addTutorRef}
        >
            <Dropdown
                label='Organization'
                data={organizations}
                dataKey='name'
                placeholder='select organization'
                value={organization?.name}
                onSelect={value => setOrganization(value)}
                required
            />
            <div className='instructors__add'>
                <TextInput
                    label='First name'
                    placeholder='enter first name'
                    value={firstName}
                    onChange={event => setFirstName(event.target.value)}
                    required
                />
                <TextInput
                    label='Last name'
                    placeholder='enter last name'
                    value={lastName}
                    onChange={event => setLastName(event.target.value)}
                    required
                />
                <TextInput
                    label='Email'
                    placeholder='enter email'
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    type='email'
                    required
                />
                <TextInput
                    label='Phone number'
                    placeholder='0000000000'
                    value={phoneNumber}
                    onChange={event => {
                        const value = event.target.value.replace(/[^\d]/g, '');
                        if (value.length <= 15) setPhoneNumber(value);
                    }}
                    type='text'
                />
                <Dropdown
                    label='Role'
                    data={ROLES}
                    value={role}
                    onSelect={value => setRole(value)}
                />
                <TextInput
                    label='Password'
                    placeholder='enter password'
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    required
                />
            </div>
        </Popup>
    );

    const successMessage = (
        <Popup
            title='Success'
            primaryButtonText='Close'
            primaryButtonOnClick={() => setSuccess('')}
            innerRef={successRef}
        >
            {success}
        </Popup>
    );

    const failedMessage = (
        <Popup
            title='Failed'
            primaryButtonText='Close'
            primaryButtonOnClick={() => setFailed('')}
            innerRef={failedRef}
        >
            {failed}
        </Popup>
    );

    return (
        <div className='instructors'>
            <Helmet>
                <title>
                    All Instructors - Test Center Admin
                </title>
            </Helmet>
            <Header title='All Instructors' />
            {failed
                ? failedMessage
                : success
                ? successMessage
                : formOpen
                ? newTutor
                : <></>
            }
            <div className='instructors__bar'>
                <TextInput
                    className='instructors__search'
                    placeholder='search instructor'
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                /> 
                {(user.role === 'Admin' || user.role === 'Super Admin') &&
                    <div className='instructors__buttons'>
                        <Button onClick={() => setFormOpen(!formOpen)}>
                            New instructor
                        </Button>
                    </div>
                }
            </div>
            <Table 
                header={['Role', 'Name', 'Instructor Email', 'Organization', 'Registered Date']}
                rows={tutors.filter(tutor => (
                    !search ||
                    tutor.email?.toLowerCase().includes(search.toLowerCase()) ||
                    tutor.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                    tutor.lastName?.toLowerCase().includes(search.toLowerCase())
                )).map(tutor => ([
                    tutor.role,
                    `${tutor.firstName} ${tutor.lastName}`,
                    tutor.email,
                    tutor.organization,
                    new Date(tutor.createdAt)
                ]))}
                onSelectCell={tutors.filter(tutor => (
                    !search ||
                    tutor.email?.toLowerCase().includes(search.toLowerCase()) ||
                    tutor.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                    tutor.lastName?.toLowerCase().includes(search.toLowerCase())
                )).map(tutor => ([
                    null,
                    () => navigate(`/instructors/${tutor.id}`),
                    null,
                    null,
                    null
                ]))}
            />
        </div>
    );
}

export default Instructors;